import { ComponentProps } from 'react'
import Missions from '../ChapterView'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { Box } from '../../../shared_components/UI/Box'
import styled, { css } from 'styled-components'
import {
  H3,
  H4,
  H6,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import { LithiumAccountSlice } from '../../../modules/user/slice'
import { useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectTaskSubmissions,
  selectXpData,
} from '../../../modules/missionControl/selectors'
import { isEmpty, isNil } from 'ramda'
import {
  Card,
  Divider,
  DropChapterCardBox,
} from '../../../shared_components/Library/Card'
import FadeIn from '../../../shared_components/Animation/FadeIn'
import { selectUser } from '../../../modules/user/selectors'
import useWeb3 from '../../../hooks/useWeb3'
import { obfuscateEthWalletAddress } from '../../../shared_components/SocialConnectButtons/Twitter'
import ProgressBar from '../../../shared_components/Library/ProgressBar'
import { useShouldShowForThreshold } from '../../../theme'
import { OverflowingCarousel } from '../../../shared_components/Library/OverflowingCarousel'
import { DropWithChapterNumber } from '..'
import {
  DropStatus,
  getDropDateStatus,
  getIsDropChapterDisabled,
} from '../helpers'
import { Link, useLocation } from 'react-router-dom'
import { useStartMission } from '../../../shared_components/Missions/MissionAction'
import useTwitterProfileImage from '../../../hooks/useTwitterProfileImage'

const INFO_POINTS = [
  {
    text: 'Complete missions each Chapter to earn XP and increase your level. 🚀',
    icon: '/img/icons/chart.svg',
  },
  {
    text: 'The higher your level, the larger your allocation in the Chapter’s airdrop.',
    icon: '/img/icons/pie.svg',
  },
  {
    text: 'If you’ve participated in a previous Chapter, you carry half of your XP into the next Chapter.',
    icon: '/img/icons/arrow-circle.svg',
  },
]

export default function DropOverview({
  drop,
  chapters,
}: {
  drop: ComponentProps<typeof Missions>['drop']
  chapters: DropWithChapterNumber[]
}) {
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')
  return (
    <>
      <Box padV={40} minW="100%">
        <FadeIn fullWidth>
          <Box
            jsb
            g={isAtLeastMediumDisplaySize ? 48 : 16}
            dc={!isAtLeastMediumDisplaySize}
          >
            <Box dc g={16} fu aw>
              <H3 s="XL" fw="semibold">
                About this airdrop
              </H3>
              {INFO_POINTS.map(({ text, icon }, index) => (
                <InfoPoint key={index} ac g={10}>
                  <img src={icon} alt="icon" />
                  <p>{text}</p>
                </InfoPoint>
              ))}
            </Box>
            <UserProgress />
          </Box>
        </FadeIn>
      </Box>
      <Divider />
      <Box padV={40} minW="100%">
        <FadeIn delay={0.3} fullWidth>
          <H3 s="XL" fw="semibold">
            Chapters
          </H3>
          <OverflowingCarousel indexGutters padV={16}>
            {chapters.slice().map((chapter, index) => (
              <ChapterCard key={index} drop={chapter} />
            ))}
          </OverflowingCarousel>
        </FadeIn>
      </Box>
    </>
  )
}

function ChapterCard({ drop }: { drop: DropWithChapterNumber }) {
  const cardImage =
    drop?.media?.projectThumbnail?.url ?? drop?.media?.topBanner?.url ?? ''
  const chapterNumber = drop?.chapterNumber ?? 0
  const { pathname } = useLocation()
  const pathHasSlash = pathname.endsWith('/')

  if (!chapterNumber) return <></>

  const { dropSettings } = drop
  const { rewardsSettings } = dropSettings ?? {}

  const description = drop?.about?.description ?? ''
  const isComingSoon = getDropDateStatus(drop) === DropStatus.ComingSoon
  const isClosed = getDropDateStatus(drop) === DropStatus.Closed

  return (
    <ChapterCardLink
      to={`${pathname}${pathHasSlash ? '' : '/'}chapter/${drop.id}`}
      disabled={getIsDropChapterDisabled(drop)}
    >
      <DropChapterCardBox g={12}>
        <img src={cardImage} alt="project thumbnail" />
        <Box dc g={4}>
          <H4 s="L" fw="semibold">
            Chapter {chapterNumber}
          </H4>
          <Span s="L" c="text-secondary">
            {description}
          </Span>
        </Box>
        <Box dc fu>
          <Box padV={12}>
            <Box key="close-state">
              <Box>
                <Span s="M" c="text-secondary"></Span>
              </Box>
              <Box fu aw g={4}>
                {isComingSoon && (
                  <Span c="text-secondary" s="M">
                    Coming soon
                  </Span>
                )}
                {isClosed && (
                  <Span c="text-secondary" s="M">
                    Closed
                  </Span>
                )}
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box padV={12} dc g={2}>
            <Box key="rewards" ac>
              <Box>
                <Span s="M" c="text-secondary">
                  Rewards
                </Span>
              </Box>
              <Box dc g={2} afe>
                {rewardsSettings?.map(
                  ({ name, image, totalSupplyForSale }, index) => (
                    <Box fu aw g={4} key={`reward-${index}`} ac>
                      {image?.url && (
                        <img
                          className="reward-img"
                          src={image.url}
                          alt={name}
                        />
                      )}
                      <span className="reward-amount-worth">
                        {totalSupplyForSale.toLocaleString()}
                      </span>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DropChapterCardBox>
    </ChapterCardLink>
  )
}

const ChapterCardLink = styled(Link)<{ disabled?: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`

const GetMultiplier = ({ taskId }: { taskId: number }) => {
  const startMission = useStartMission(taskId)

  return (
    <ThemedButton $styleType="secondary" onClick={startMission}>
      Get Multiplier
    </ThemedButton>
  )
}

export function UserProgress({
  showGetMultiplierButton = false,
  showTotalXp = false,
  showMissionsAvailable = true,
  showMissionsCompleted = true,
}: {
  showGetMultiplierButton?: boolean
  showTotalXp?: boolean
  showMissionsAvailable?: boolean
  showMissionsCompleted?: boolean
}) {
  const xpData = useSelector(selectXpData)
  const user = useSelector(selectUser)
  const { initWeb3 } = useWeb3()

  const currentProject = useSelector(selectCurrentProject)
  const taskSubmissions = useSelector(selectTaskSubmissions)
  const tasks = currentProject?.tasks ?? []

  const { url } = useTwitterProfileImage(user)

  if (!user)
    return (
      <ConnectCard>
        <Strong>Connect your wallet to start earning XP</Strong>
        <ThemedButton $styleType="primary" onClick={initWeb3}>
          Connect wallet
        </ThemedButton>
      </ConnectCard>
    )

  if (isEmpty(xpData) || isNil(xpData)) return null

  const { level, currentLevelCeiling, currentLevelFloor, totalXp } = xpData
  const xpDifferenceBetweenCeilingAndFloor =
    currentLevelCeiling - currentLevelFloor
  const progressToLevel =
    ((totalXp - currentLevelFloor) / xpDifferenceBetweenCeilingAndFloor) * 100
  const username = getUsernameFromUserObject(user)
  const handle = handleFromUserObject(user)

  const totalMissionsCount = tasks.length
  const missionsCompletedCount = Object.keys(taskSubmissions).length

  const completedTaskIds = Object.keys(taskSubmissions)
  const [completedTaskWithMultiplierReward] = tasks
    .filter(
      (task) =>
        completedTaskIds.includes(task.subtasks[0]?.id.toString()) &&
        task.multiplierReward > 0,
    )
    .sort((a, b) => b.multiplierReward - a.multiplierReward)

  const activeMissions = tasks.filter(
    (task) => !completedTaskIds.includes(task.subtasks[0]?.id.toString()),
  )
  const activeMultiplierMission = activeMissions.find(
    (task) => task.multiplierReward > 0,
  )

  const hasActiveMultiplierMission = !!activeMultiplierMission

  const renderGetMultiplierButton =
    showGetMultiplierButton &&
    hasActiveMultiplierMission &&
    !completedTaskWithMultiplierReward

  return (
    <Box dc g={16}>
      <ProgressCard>
        <Box ac g={11}>
          <Avatar src={url} alt="avatar" />
          <Box dc>
            <H4 fw="semibold" s="L">
              {username}
            </H4>
            {handle && (
              <Span c="text-secondary" s="M">
                {handle}
              </Span>
            )}
          </Box>
        </Box>
        <Box dc>
          <Box jfe>
            <Span
              c="text-brandPrimary"
              fw="semibold"
              s="M"
            >{`${totalXp} XP`}</Span>{' '}
            <Span c="text-secondary" s="M">
              {' '}
              / {currentLevelCeiling}XP
            </Span>
          </Box>
          <ProgressBar pctProgress={progressToLevel} leftCircleAmount={level} />
        </Box>
      </ProgressCard>
      <MultipleCardsLayout>
        {showTotalXp && (
          <ProgressCard pad={16}>
            <Box dc g={4}>
              <Span c="text-secondary" s="M">
                Total XP
              </Span>
              <Box ac g={6}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.798 0.563681L12 0.500686L13.202 0.563681L14.3908 0.751974L15.5535 1.0635L16.6772 1.49485L17.7497 2.0413L18.7591 2.69686L19.6945 3.45435L20.5457 4.30546L21.3031 5.24087L21.9587 6.25035L22.5051 7.3228L22.9365 8.44652L23.248 9.60915L23.4363 10.798L23.4993 12L23.4363 13.202L23.248 14.3908L22.9365 15.5535L22.5051 16.6772L21.9587 17.7496L21.3031 18.7591L20.5457 19.6945L19.6945 20.5457L18.7591 21.3031L17.7496 21.9587L16.6772 22.5051L15.5535 22.9365L14.3908 23.248L13.202 23.4363L12 23.4993L10.798 23.4363L9.60915 23.248L8.44652 22.9365L7.3228 22.5051L6.25035 21.9587L5.24087 21.3031L4.30546 20.5457L3.45435 19.6945L2.69686 18.7591L2.0413 17.7497L1.49485 16.6772L1.0635 15.5535L0.751974 14.3908L0.563681 13.202L0.500686 12L0.563681 10.798L0.751974 9.60916L1.0635 8.44652L1.49485 7.32281L2.0413 6.25034L2.69686 5.24087L3.45434 4.30546L4.30546 3.45434L5.24087 2.69686L6.25034 2.0413L7.32281 1.49485L8.44652 1.0635L9.60916 0.751974L10.798 0.563681Z"
                    stroke="white"
                  />
                  <path
                    d="M9.99166 16L8.52643 13.5499L7.06119 16H4.20277L6.88104 11.8925L4.38293 7.98923H7.22933L8.52643 10.2712L9.7995 7.98923H12.6699L10.1718 11.8805L12.8501 16H9.99166ZM13.2776 16V7.98923H17.5532C18.0096 7.98923 18.4299 8.0613 18.7902 8.20542C19.1505 8.34954 19.4508 8.55371 19.691 8.80592C19.9312 9.05814 20.1233 9.34638 20.2434 9.68266C20.3635 10.0189 20.4356 10.3672 20.4356 10.7396C20.4356 11.1239 20.3635 11.4722 20.2434 11.7964C20.1233 12.1327 19.9312 12.421 19.691 12.6732C19.4508 12.9254 19.1505 13.1176 18.7902 13.2617C18.4299 13.4058 18.0096 13.4779 17.5532 13.4779H15.7036V16H13.2776ZM17.2409 11.3641C17.4451 11.3641 17.6132 11.316 17.7573 11.208C17.9015 11.1119 17.9735 10.9557 17.9735 10.7396C17.9735 10.5234 17.9015 10.3672 17.7573 10.2592C17.6132 10.1631 17.4451 10.103 17.2409 10.103H15.7036V11.3641H17.2409Z"
                    fill="white"
                  />
                </svg>

                <H6 s="L" fw="semibold">
                  {totalXp.toLocaleString()}
                </H6>
              </Box>
            </Box>
          </ProgressCard>
        )}
        {showMissionsAvailable && (
          <ProgressCard pad={16}>
            <Box dc g={4}>
              <Span c="text-secondary" s="M">
                Missions available
              </Span>
              <Box ac g={6}>
                <img src="/img/icons/rocket.svg" alt="rocket" />
                <H6 s="L" fw="semibold">
                  {totalMissionsCount}
                </H6>
              </Box>
            </Box>
          </ProgressCard>
        )}
        {showMissionsCompleted && (
          <ProgressCard pad={16}>
            <Box dc g={4}>
              <Span c="text-secondary" s="M">
                Missions completed
              </Span>
              <Box ac g={6}>
                <img src="/img/icons/green-check.svg" alt="rocket" />
                <H6 s="L" fw="semibold">
                  {missionsCompletedCount}
                </H6>
              </Box>
            </Box>
          </ProgressCard>
        )}
        <ProgressCard pad={16}>
          <Box dc g={4}>
            <Span c="text-secondary" s="M">
              Multiplier
            </Span>
            <Box ac g={6}>
              {renderGetMultiplierButton ? (
                <div style={{ paddingTop: '4px' }}>
                  <GetMultiplier taskId={activeMultiplierMission.id} />
                </div>
              ) : (
                <>
                  <img src="/img/icons/white-arrow-up.svg" alt="rocket" />
                  <H6 s="L" fw="semibold">
                    {completedTaskWithMultiplierReward?.multiplierReward ?? 1}
                  </H6>
                </>
              )}
            </Box>
          </Box>
        </ProgressCard>
      </MultipleCardsLayout>
    </Box>
  )
}

export const Avatar = styled.img<{ size?: number }>`
  width: var(--units-60, 60px);
  border-radius: var(--units-4, 4px);
  height: var(--units-60, 60px);
  background-color: ${({ theme }) =>
    theme.semantics.zap.surface.minimalPrimary};
  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}
`

const ProgressCard = styled(Card)`
  width: 100%;
`

const ConnectCard = styled(Card)`
  width: 100%;

  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  text-align: center;
`

const InfoPoint = styled(Box)`
  color: var(--text-secondary, #b1b1b1);
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-L-L-size, 16px);
  font-style: normal;
  font-weight: var(--type-weight-Regular, 300);
  line-height: var(--Text-L-L-line-height, 20px); /* 125% */
  max-width: 350px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.medium}px) {
    max-width: unset;
  }
`

export const LeaderboardBox = styled(Box)`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(3, 11, 17, 0.9);
  padding: 16px;
  min-height: 285px;

  max-width: 554px;
  @media screen and (max-width: 1400px) {
    max-width: none;
    min-height: 150px;
  }
`

export const LeaderboardEmptyBox = styled(Box)`
  padding: 16px;
  min-height: 285px;
  @media screen and (max-width: 1400px) {
    min-height: 150px;
  }
`

export const LeaderboardEmptyText = styled.p`
  color: #fff;
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
  letter-spacing: -0.8px;
  text-align: center;
  margin: 0;
`

export const MultipleCardsLayout = styled.div`
  width: 100%;
  flex-wrap: wrap;
  display: flex;
  gap: 16px;

  > * {
    flex: 1 1 173px;
    min-width: 173px;
  }
`

export function getUsernameFromUserObject(user: LithiumAccountSlice, address?: string) {
  return !!user?.username
    ? user?.username
    : !!user?.twitterHandle
    ? user?.twitterHandle
    : !!user?.discordHandle
      ? user?.discordHandle
      : !!user?.telegramHandle
        ? user?.telegramHandle
        : address ? obfuscateEthWalletAddress(address) : ''
}

export function handleFromUserObject(user: LithiumAccountSlice) {
  return !!user?.twitterHandle
    ? `@${user?.twitterHandle}`
    : !!user?.discordHandle
      ? `@${user?.discordHandle}`
      : !!user?.telegramHandle
        ? `@${user?.telegramHandle}`
        : ''
}
